import Hls, { type ErrorData, type HlsConfig } from 'hls.js';

type RetryController = {
  getPlaylistRetryOrSwitchAction(data: ErrorData, level: number | null | undefined): NonNullable<ErrorData['errorAction']>;
};

/** Compatibility for hls.js 1.6.15's missing audio playlist timeout classification. */
export function createPlaylistRetryController(): HlsConfig['errorController'] | undefined {
  const Parent = Hls.DefaultConfig?.errorController;
  if (Hls.version !== '1.6.15' || typeof Parent !== 'function') return undefined;
  // This hook is private upstream; keep the adapter version-scoped and test it
  // against the real controller. It retains upstream retry counts and failover.
  const Base = Parent as unknown as new (hls: Hls) => RetryController;
  return class PlaylistRetryController extends Base {
    getPlaylistRetryOrSwitchAction(data: ErrorData, level: number | null | undefined) {
      if (data.details !== Hls.ErrorDetails.AUDIO_TRACK_LOAD_TIMEOUT) {
        return super.getPlaylistRetryOrSwitchAction(data, level);
      }
      const details = data.details;
      try {
        // Normalize only while selecting the policy; listeners still receive
        // the original audio detail and context. timeoutRetry schedules a fast
        // non-blocking reload via AudioTrackController's existing retry branch.
        data.details = Hls.ErrorDetails.LEVEL_LOAD_TIMEOUT;
        return super.getPlaylistRetryOrSwitchAction(data, level);
      } finally {
        data.details = details;
      }
    }
  } as unknown as HlsConfig['errorController'];
}
